import { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Card } from 'shared/ui/card/card';
import cls from './modal.module.scss';
import IconButton from '../iconButton/icon-button';
import { ICONS } from 'shared/constants/images';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number; // pixels
  variant?: 'center' | 'drawer' | 'fullscreen';
  drawerWidth?: number; // alias for width when drawer variant (px)
  className?: string;
  overlayClose?: boolean;
  closeButton?: boolean;
  closeButtonLabel?: string;
  onBack?: () => void;
}

export const Modal: FC<ModalProps> = (props: ModalProps) => {
  const {
    isOpen,
    onClose,
    children,
    width,
    variant = 'center',
    drawerWidth,
    className,
    closeButton = false,
    closeButtonLabel,
    overlayClose = true,
    onBack,
  } = props;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (overlayClose && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  if (variant === 'fullscreen') {
    const handleTopControlClick = onBack ?? onClose;
    const topControlLabel = onBack ? 'Back' : 'Close';

    const fullscreenContent = (
      <div
        className={`${cls.fullscreenOverlay} blueLight_theme`.trim()}
        role='presentation'
        aria-label='modal window'
      >
        <div className={`${cls.fullscreenRoot} ${className || ''}`}>
          <IconButton
            className={cls.fullscreenControl}
            dense
            pathToIcon={onBack ? ICONS.arrowBack : ICONS.crossBlack}
            iconSize={onBack ? 21 : 14}
            aria-label={topControlLabel}
            onClick={handleTopControlClick}
          />
          <div className={cls.fullscreenContent}>{children}</div>
        </div>
      </div>
    );

    return createPortal(fullscreenContent, document.body);
  }

  const isDrawer = variant === 'drawer';
  const overlayClass = isDrawer ? cls.overlayRight : cls.overlay;
  const resolvedDrawerWidth = drawerWidth || width || 480; // default drawer width px
  const panelStyle: React.CSSProperties = isDrawer
    ? { width: resolvedDrawerWidth }
    : { width };

  const panelClass = isDrawer ? cls.modalDrawer : cls.modal;

  const dialogAriaLabel = isDrawer ? 'drawer panel' : 'modal window';

  const modalContent = (
    <div
      className={`${overlayClass} blueLight_theme`.trim()}
      onClick={handleOverlayClick}
      role='presentation'
      aria-label={dialogAriaLabel}
    >
      <Card className={`${panelClass} ${className || ''}`} style={panelStyle}>
        {closeButton && (
          <IconButton
            className={cls.closeBtn}
            dense
            pathToIcon={ICONS.crossBlack}
            onClick={onClose}
            aria-label={closeButtonLabel}
          />
        )}
        {children}
      </Card>
    </div>
  );

  return createPortal(modalContent, document.body);
};
